/**
 * StatusBanner - single general-purpose banner for status messages.
 *
 * Usage -- standard (status required):
 *   <StatusBanner status="success" message="Saved successfully." />
 *   <StatusBanner status="error"   message="Failed to save." />
 *   <StatusBanner status="info"    layout="inline">Rich <strong>content</strong></StatusBanner>
 *
 * Usage -- preset (zero-config for well-known constant banners):
 *   <StatusBanner preset="readOnly" />
 *   <StatusBanner preset="signedLocked" />
 *
 * The discriminated union enforces that you supply EITHER preset OR status -
 * never both, never neither.
 *
 * Replaces:
 *   - BannerIcon (secondary) - absorbed here
 *   - ReadOnlyBanner wrapper - one-liner over preset="readOnly"
 *   - InfoBanner wrapper     - one-liner over status="info"
 *   - Scattered alert alert-success / alert-danger divs across the codebase
 */
import React from "react";
import { ReactComponent as OkSvg } from "../../../static/images/banner-ok.svg";
import { ReactComponent as WarnSvg } from "../../../static/images/banner-warn.svg";
import { ReactComponent as InfoSvg } from "../../../static/images/banner-info.svg";
import { ReactComponent as ErrorSvg } from "../../../static/images/banner-error.svg";
import { ReactComponent as EyeSvg } from "../../../static/images/banner-eye.svg";
import { ReactComponent as LockSvg } from "../../../static/images/banner-lock.svg";
import constants from "../../../constants/constants.json";
import styles from "./StatusBanner.module.scss";

export type BannerStatus = "success" | "error" | "warning" | "info";
export type BannerPreset = "readOnly" | "signedLocked";
export type BannerLayout = "full" | "inline";

type PresetProps = {
  preset: BannerPreset;
  status?: never;
  message?: string;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  layout?: BannerLayout;
  className?: string;
};

type CustomProps = {
  preset?: never;
  status: BannerStatus;
  message?: string;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  layout?: BannerLayout;
  className?: string;
};

export type StatusBannerProps = PresetProps | CustomProps;

const ICON_MAP: Record<BannerStatus, React.FC<React.SVGProps<SVGSVGElement>>> = {
  success: OkSvg,
  error: ErrorSvg,
  warning: WarnSvg,
  info: InfoSvg,
};

interface PresetConfig {
  status: BannerStatus;
  message: string;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
}

const PRESET_MAP: Record<BannerPreset, PresetConfig> = {
  readOnly: {
    status: "warning",
    message: constants.assessment.readOnlyBanner,
    Icon: EyeSvg,
  },
  signedLocked: {
    status: "info",
    message: constants.assessment.assessmentSignedLoked,
    Icon: LockSvg,
  },
};

const BANNER_CLASS: Record<BannerStatus, string> = {
  success: styles.banner_success,
  error: styles.banner_error,
  warning: styles.banner_warning,
  info: styles.banner_info,
};

// ---- Component ----

export const StatusBanner: React.FC<StatusBannerProps> = ({ preset, status, message, children, icon, layout = "full", className }) => {
  const config = preset ? PRESET_MAP[preset] : null;
  const resolvedStatus = (config?.status ?? status) as BannerStatus;
  const resolvedMessage = message ?? config?.message;

  const DefaultIcon = config ? config.Icon : ICON_MAP[resolvedStatus];
  const resolvedIcon = icon ?? <DefaultIcon className={styles.icon} aria-hidden="true" />;

  const containerClass = [styles.banner, BANNER_CLASS[resolvedStatus], layout === "inline" ? styles.layout_inline : styles.layout_full, className]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClass} role={resolvedStatus === "error" || resolvedStatus === "warning" ? "alert" : undefined}>
      {resolvedIcon}
      {resolvedMessage && <span className={styles.message}>{resolvedMessage}</span>}
      {children && <div className={styles.children}>{children}</div>}
    </div>
  );
};
